import API_BASE_URL from '../config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Drawer,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Typography,
    IconButton,
    useTheme,
    alpha,
    Collapse,
    CircularProgress
} from '@mui/material';
import {
    Home,
    Store,
    Assessment,
    CardGiftcard,
    Calculate,
    School,
    Info,
    Phone,
    ShoppingCart,
    Receipt,
    Close,
    ExpandLess,
    ExpandMore,
    Category,
    ViewList
} from '@mui/icons-material';

const BASE_URL = API_BASE_URL;

const MobileDrawer = ({ drawerOpen, setDrawerOpen, t, language }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    // Store categories state
    const [storeExpanded, setStoreExpanded] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    const handleNavigation = (path) => {
        navigate(path);
        setDrawerOpen(false); // Close drawer after navigation
    };

    const handleCategoryNavigation = (categoryId) => {
        navigate(`/store?category=${categoryId}`);
        setDrawerOpen(false);
    };

    const handleAllProductsNavigation = () => {
        navigate('/store');
        setDrawerOpen(false);
    };

    const isActive = (path) => location.pathname === path;
    const isStoreActive = () => location.pathname === '/store';

    // Fetch categories function (similar to AppHeader)
    const fetchCategories = async () => {
        setCategoriesLoading(true);
        try {
            const response = await fetch(`${BASE_URL}/api/categories`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setCategories(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Helper function for localized category names (similar to AppHeader)
    const getLocalizedCategoryName = (category) => {
        if (typeof category.name === 'object' && category.name !== null) {
            return category.name[language] || category.name.en || '';
        }
        return category.name || '';
    };

    // Fetch categories on mount
    useEffect(() => {
        fetchCategories();
    }, []);

    // Re-fetch categories when language changes (THIS WAS MISSING)
    useEffect(() => {
        if (language) {
            fetchCategories();
        }
    }, [language]);

    // Auto-expand store if on store page
    useEffect(() => {
        if (isStoreActive()) {
            setStoreExpanded(true);
        }
    }, [location.pathname]);

    const handleStoreClick = () => {
        setStoreExpanded(!storeExpanded);
        // If not expanded and categories not loaded, navigate to store
        if (!storeExpanded && categories.length === 0) {
            handleNavigation('/store');
        }
    };

    const menuItems = [
        {
            text: t?.home || 'Home',
            icon: <Home />,
            path: '/',
            primary: true
        },
        // {
        //     text: t?.cart || 'Cart',
        //     icon: <ShoppingCart />,
        //     path: '/cart'
        // },
        // {
        //     text: t?.allOrders || 'Orders',
        //     icon: <Receipt />,
        //     path: '/orders'
        // },
    ];

    const secondaryItems = [
        // {
        //     text: t?.about || 'About',
        //     icon: <Info />,
        //     path: '/about'
        // },
        // {
        //     text: t?.contact || 'Contact',
        //     icon: <Phone />,
        //     path: '/contact'
        // },
    ];

    const ListItemComponent = ({ item, onClick }) => (
        <ListItem
            button
            onClick={onClick}
            sx={{
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                minHeight: 48,
                backgroundColor: isActive(item.path)
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                },
                color: isActive(item.path)
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
            }}
        >
            <ListItemIcon
                sx={{
                    color: 'inherit',
                    minWidth: 40
                }}
            >
                {item.icon}
            </ListItemIcon>
            <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 400,
                    fontSize: '0.95rem'
                }}
            />
        </ListItem>
    );

    const StoreListItem = () => (
        <>
            <ListItem
                button
                onClick={handleStoreClick}
                sx={{
                    borderRadius: 2,
                    mx: 1,
                    mb: 0.5,
                    minHeight: 48,
                    backgroundColor: isStoreActive()
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    },
                    color: isStoreActive()
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                }}
            >
                <ListItemIcon
                    sx={{
                        color: 'inherit',
                        minWidth: 40
                    }}
                >
                    <Store />
                </ListItemIcon>
                <ListItemText
                    primary={t?.store || 'Store'}
                    primaryTypographyProps={{
                        fontWeight: isStoreActive() ? 600 : 400,
                        fontSize: '0.95rem'
                    }}
                />
                {storeExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={storeExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {/* All Products Option */}
                    <ListItem
                        button
                        onClick={handleAllProductsNavigation}
                        sx={{
                            borderRadius: 2,
                            mx: 2,
                            mb: 0.5,
                            pl: 4,
                            minHeight: 40,
                            backgroundColor: (location.pathname === '/store' && !location.search.includes('category='))
                                ? alpha(theme.palette.primary.main, 0.08)
                                : 'transparent',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <ViewList fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary={t?.allProducts || 'All Products'}
                            primaryTypographyProps={{
                                fontSize: '0.875rem'
                            }}
                        />
                    </ListItem>

                    {/* Categories Loading State */}
                    {categoriesLoading && (
                        <ListItem sx={{ pl: 4, py: 1 }}>
                            <CircularProgress size={16} sx={{ mr: 2 }} />
                            <ListItemText
                                primary={t?.loading || 'Loading...'}
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    color: 'text.secondary'
                                }}
                            />
                        </ListItem>
                    )}

                    {/* Category Options */}
                    {!categoriesLoading && categories.map((category) => {
                        const isCurrentCategory = location.search.includes(`category=${category._id}`);

                        return (
                            <ListItem
                                key={category._id}
                                button
                                onClick={() => handleCategoryNavigation(category._id)}
                                sx={{
                                    borderRadius: 2,
                                    mx: 2,
                                    mb: 0.5,
                                    pl: 4,
                                    minHeight: 40,
                                    backgroundColor: isCurrentCategory
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : 'transparent',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <Category fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={getLocalizedCategoryName(category)}
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem'
                                    }}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </Collapse>
        </>
    );

    return (
        <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
                '& .MuiDrawer-paper': {
                    width: {
                        xs: '85vw',
                        sm: 280,
                    },
                    maxWidth: 320,
                    backgroundColor: theme.palette.background.paper,
                    paddingTop: {
                        xs: 'max(8px, env(safe-area-inset-top))',
                        sm: 0
                    },
                    paddingBottom: {
                        xs: 'max(8px, env(safe-area-inset-bottom))',
                        sm: 0
                    },
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch'
                }
            }}
            ModalProps={{
                keepMounted: true,
            }}
        >
            <Box sx={{ width: '100%', height: '100%' }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t?.welcome || 'Menu'}
                    </Typography>
                    <IconButton
                        onClick={() => setDrawerOpen(false)}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            p: { xs: 1.5, sm: 1 }
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>

                {/* Main Navigation */}
                <List sx={{ px: 1, py: 2 }}>
                    {/* Home Item */}
                    <ListItemComponent
                        item={menuItems[0]}
                        onClick={() => handleNavigation(menuItems[0].path)}
                    />

                    {/* Store Item with Categories */}
                    <StoreListItem />

                    {/* Other Menu Items */}
                    {menuItems.slice(1).map((item, index) => (
                        <ListItemComponent
                            key={index}
                            item={item}
                            onClick={() => handleNavigation(item.path)}
                        />
                    ))}
                </List>

                <Divider sx={{ mx: 2 }} />

                {/* Secondary Navigation */}
                <List sx={{ px: 1, py: 1 }}>
                    {secondaryItems.map((item, index) => (
                        <ListItemComponent
                            key={index}
                            item={item}
                            onClick={() => handleNavigation(item.path)}
                        />
                    ))}
                </List>

                {/* Footer */}
                <Box sx={{ mt: 'auto', p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        {t?.version || 'Version 1.0.0'}
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
};

export default MobileDrawer;